// Classify a credit row by what drives it, and recover the "expected" unit
// price for price-difference credits.
//
// A credit row carries two independent drivers (see server syncCredits):
//   credit_qty        — returned units (a quantity credit)
//   credit_price_diff — per-unit overcharge vs the agreed/catalog price
// Both can be set on the same row (a "combined" credit).
//
// For a price-difference credit the invoice charged `unit_price`; the price it
// SHOULD have been is `unit_price − credit_price_diff` (the per-unit diff is the
// overcharge that was filled from curPrice − anchorPrice on the document).

import { round2 } from './money';

export function creditKind(c) {
  const q = (parseFloat(c?.credit_qty) || 0) > 0;
  const p = (parseFloat(c?.credit_price_diff) || 0) > 0;
  if (q && p) return 'combined';
  if (p) return 'price';
  return 'qty';
}

// Which of the per-item numeric columns each kind actually populates. The
// credits table is partitioned by kind, so a shared header would force every
// group to carry the union — an entire column of '—' on each group, and a table
// too wide for a 13" laptop. Each group renders only its live columns.
export const KIND_COLS = {
  qty:      { qty: true,  diff: false, expected: false },
  price:    { qty: false, diff: true,  expected: true  },
  combined: { qty: true,  diff: true,  expected: true  },
};

// The column order for one group's table, as keys. Built as a list rather than
// written out as markup twice so the subtotal row's colSpan is derived from the
// real column set instead of a hand-counted constant that drifts when a column
// is added.
//
// There is deliberately no column for the close audit (who closed the credit,
// when and why). It briefly had one and the table was already at the edge of a
// 13" laptop; the audit now lives behind the row's ⋮ "הערות" item on desktop
// and in the mobile detail sheet — see CreditClosedBy.jsx.
export function columnKeys(cols, selectable = false) {
  return [
    ...(selectable ? ['select'] : []),
    'supplier', 'doc', 'date', 'item', 'sku',
    ...(cols.qty      ? ['qty']      : []),
    ...(cols.diff     ? ['diff']     : []),
    'unit',
    ...(cols.expected ? ['expected'] : []),
    'net', 'gross', 'status', 'actions',
  ];
}

// The unit price the supplier should have charged: charged − per-unit diff.
// Equals the charged price when there is no diff (pure quantity credit).
// Note: exact only when the line had no per-line discount, since the credit
// row stores unit_price + diff but not the discount.
export function expectedUnitPrice(c) {
  return round2((parseFloat(c?.unit_price) || 0) - (parseFloat(c?.credit_price_diff) || 0));
}
